import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import { Button, Spinner } from 'react-bootstrap'
import ConfirmationModal from 'components/ConfirmationModal'
import CustomModal from 'components/CustomModal/CustomModal'
import { useSwitchPlan } from '../../../hooks/useSwitchPlan'
import { SwitchPlanModal } from './SwitchPlanModal'
import { PLANS_ID } from '../../../constants/plans'
import { getAPIKey, manageBilling, uncancelSubscription } from '../../../services/manage'
import {
  copyToClipboard,
  hasFeature,
  isOnFreeTrial,
  getProjectsToArchive,
  buildCancelMessage
} from '../../../utils/general'
import CustomTooltip from '../../CustomTooltip'
import RecycleApiKeyModal from './RecycleApiKeyModal'

import { fetchUserData, setAPIKey, setAccountPasswordModal, showMessageModal } from '../../../Manage/actions'
import { F_API_KEY } from '../../../constants/plans/constants'
import { isIndia } from '../../../constants/currency'
import { getPaymentProvider } from '../../../utils/paymentProvider'
import { cancelFreeTrialSubscription } from '../../../services/payment'
import { useLocation, useHistory } from 'react-router-dom'
import DEEP_LINK from '../../../utils/deepLinks'
import { ACCOUNT_STATUS } from '../../../constants'
import celloService from '../../../services/cello'
import { canCancelSubscription } from '../../../utils/subscription'
import useEnterpriseSso from 'hooks/useEnterpriseSso'
import { openEnterpriseEnquiryCalendar } from 'utils/calEmbed'

const fetchBillingLink = () => {
  manageBilling()
    .then((res) => {
      const url = res.data.session.url
      if (url) {
        window.open(url, '_blank')
      }
    })
    .catch((e) => {
      console.error(e)
      alert('Sorry, an error has occurred')
    })
}

const Details = ({
  cardType,
  productId,
  lastFour,
  status,
  userCategory,
  amount,
  interval,
  nextBilling,
  email,
  apiKey,
  currency = '$',
  customerId,
  stripeCustomer,
  updateUrl,
  onCancel,
  onUpgrade,
  dispatch,
  trialEndDate,
  childAccount,
  liveSites,
  downgradePlan
}) => {
  const [generatingKey, setGeneratingKey] = useState(false)
  const [modalConfirmationShow, setModalConfirmationShow] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [cancelTrialModalShow, setCancelTrialModalShow] = useState(false)
  const [cancelTrialLoading, setCancelTrialLoading] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showRecycleModal, setShowRecycleModal] = useState(false)
  const [ssoSetupOpened, setSsoSetupOpened] = useState(false)

  const isFreeTrial = isOnFreeTrial(status ? { status } : {})
  const {
    loading: ssoLoading,
    checking: ssoChecking,
    openingSetup: ssoOpeningSetup,
    entitled: ssoEntitled,
    connected: ssoConnected,
    checkStatus: checkSsoStatus,
    openSetup: openSsoSetup
  } = useEnterpriseSso({ enabled: !childAccount })

  const handleOpenSsoSetup = async ({ regenerate = false } = {}) => {
    try {
      await openSsoSetup({ regenerate })
      setSsoSetupOpened(true)
    } catch (e) {
      dispatch(
        showMessageModal({
          title: 'SSO setup',
          message: e?.message || 'Failed to open SSO setup. Please try again.',
          error: true
        })
      )
    }
  }

  const handleFinishedSsoSetup = async () => {
    const result = await checkSsoStatus()
    if (result?.error) {
      dispatch(
        showMessageModal({
          title: 'SSO setup',
          message: 'Could not verify SSO status. Please try again.',
          error: true
        })
      )
      return
    }
    if (!result?.connected) {
      dispatch(
        showMessageModal({
          title: 'SSO setup',
          message: 'SSO connection not set up. Finish setup using your existing link, or via the link below.',
          secondaryLabel: 'Continue setup',
          onSecondaryClick: () => handleOpenSsoSetup(),
          ctaLabel: 'Done'
        })
      )
      return
    }
    dispatch(fetchUserData(false))
  }

  const {
    switchPlanSuggestionModalShow,
    setSwitchPlanSuggestionModalShow,
    confirmSwitchPlanModalShow,
    setconfirmSwitchPlanModalShow,
    switchPlanProcessing,
    eligiblePlanWithPrices,
    currentPlanWithPrices,
    handleCancelClick,
    handleCancelTrialClick,
    handleConfirmSwitchPlanClick,
    onContinueCancellation,
    instantSwitchPlan,
    isDowngradeScheduled,
    nextBillingLabel
  } = useSwitchPlan({
    isOnFreeTrial: isFreeTrial,
    liveSites,
    productId,
    currency,
    customerId,
    cardType,
    interval,
    nextBilling,
    email,
    dispatch,
    setCancelTrialModalShow,
    onCancel
  })

  const planLabel = PLANS_ID[productId]?.label
  const trialAccount = !productId
  const isB2C = userCategory === 'B2C'

  const formatTrialDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const displayPrice = isFreeTrial ? `${currency}0.00 (Trial)` : `${currency}${amount}`
  const displayStatus = isFreeTrial ? (isB2C ? 'Solo Trial Active' : 'Pro Trial Active') : status
  const billingLabel = isFreeTrial ? 'Trial ends' : 'Next billing'
  const billingDate = isFreeTrial ? formatTrialDate(trialEndDate) : nextBilling

  const location = useLocation()
  const history = useHistory()

  const isCancelling = status?.startsWith('CANCELLING') || status === 'CANCELLED'

  useEffect(() => {
    if (location.pathname === DEEP_LINK.REACTIVATE_SUB) {
      reactivateSubscription()
      history.replace({ pathname: '/manage' })
    }
  }, [])

  const generateAPIKey = () => {
    setGeneratingKey(true)
    getAPIKey()
      .then(({ data: { key } }) => {
        dispatch(setAPIKey(key))
        setShowRecycleModal(false)
      })
      .catch((e) => {
        console.error(e)
        alert(`Sorry, an error has occurred`)
      })
      .finally(() => setGeneratingKey(false))
  }

  const handleUncancelClick = () => {
    setConfirmMessage('Are you sure you want to uncancel your subscription?') // Set confirmation message
    setModalConfirmationShow(true)
  }

  const reactivateSubscription = () => {
    if (isCancelling) {
      uncancelSubscription()
        .then(() => {
          dispatch(fetchUserData(false))
          dispatch(showMessageModal({ title: 'Subscription Reactivated' }))
        })
        .catch((error) => {
          console.error(error)
          alert('Failed to uncancel subscription.')
        })
        .finally(() => {
          setModalConfirmationShow(false)
        })
    }
  }

  const cancelFreeTrial = () => {
    setCancelTrialLoading(true)
    cancelFreeTrialSubscription({ email })
      .then(() => {
        // Cancel was successful, show success modal instead of logging out immediately
        setCancelTrialModalShow(false)
        setShowSuccessModal(true)
      })
      .catch((error) => {
        console.error(error)
        dispatch(
          showMessageModal({
            title: 'Error',
            message: 'Failed to cancel trial. Please try again.',
            success: false
          })
        )
        setCancelTrialModalShow(false)
      })
      .finally(() => {
        setCancelTrialLoading(false)
      })
  }

  const handleSuccessModalDone = () => {
    dispatch(fetchUserData(false))
    setShowSuccessModal(false)
    celloService.clearToken()
    localStorage.removeItem('b2bFreeTrialAlertClosed')
  }

  const { toArchive: projectsToArchive, toRemove: projectsToRemove } = getProjectsToArchive(liveSites)
  const exceedsLimits = projectsToArchive.length > 0 || projectsToRemove.length > 0
  const archivedSubdomains = projectsToArchive.map((p) => p.subdomain)
  const removedSubdomains = projectsToRemove.map((p) => p.subdomain)

  if (switchPlanSuggestionModalShow || confirmSwitchPlanModalShow) {
    return (
      <SwitchPlanModal
        currency={currency}
        interval={interval}
        switchPlanSuggestionModalShow={switchPlanSuggestionModalShow}
        setSwitchPlanSuggestionModalShow={setSwitchPlanSuggestionModalShow}
        confirmSwitchPlanModalShow={confirmSwitchPlanModalShow}
        setconfirmSwitchPlanModalShow={setconfirmSwitchPlanModalShow}
        switchPlanProcessing={switchPlanProcessing}
        eligiblePlanWithPrices={eligiblePlanWithPrices}
        currentPlanWithPrices={currentPlanWithPrices}
        setCancelTrialModalShow={setCancelTrialModalShow}
        onContinueCancellation={onContinueCancellation}
        handleConfirmSwitchPlanClick={handleConfirmSwitchPlanClick}
        instantSwitchPlan={instantSwitchPlan}
        isDowngradeScheduled={isDowngradeScheduled}
        nextBillingLabel={nextBillingLabel}
      />
    )
  }

  return (
    <>
      <ConfirmationModal
        title="Uncancel Subscription"
        message={confirmMessage}
        show={modalConfirmationShow}
        handleNegative={() => setModalConfirmationShow(false)}
        handlePositive={reactivateSubscription}
      />

      <ConfirmationModal
        title="Cancel Free Trial"
        message={
          exceedsLimits
            ? buildCancelMessage(archivedSubdomains, removedSubdomains)
            : 'Your account will be downgraded to the Free plan. Are you sure you want to cancel your free trial?'
        }
        show={cancelTrialModalShow}
        handleNegative={() => setCancelTrialModalShow(false)}
        handlePositive={cancelFreeTrial}
        actionProcessing={cancelTrialLoading}
      />

      <RecycleApiKeyModal
        isOpen={showRecycleModal}
        onClose={() => setShowRecycleModal(false)}
        onSuccess={() => generateAPIKey(true)}
        isLoading={generatingKey}
      />

      <CustomModal
        aria-labelledby="contained-modal-title-vcenter"
        show={showSuccessModal}
        animation={false}
        centered
        enforceFocus={false}
      >
        <div className="ps-3 pe-3">
          <div className="text-center mb-4 mt-4">
            <h4 className="mt-3 mb-4">Downgraded to Free Plan</h4>
            <p className="grey w-75 m-auto">
              Your free trial has been cancelled. Your account is still active on the Free plan.
            </p>
          </div>
          <Button className="action-btn mb-3 w-100" onClick={handleSuccessModalDone}>
            Done
          </Button>
        </div>
      </CustomModal>
      <h4 className="color-purple mb-3 bold">Account Management</h4>

      <div className="account-section">
        <div className="account-section-title">Sign-in & security</div>
        <table className="account-table w-100 grey">
          <tbody>
            <tr>
              <td>{childAccount ? 'Admin email' : 'Email'}</td>
              <td className="text-end bold text-break">{email}</td>
            </tr>
            {!childAccount && (
              <tr>
                <td>Password</td>
                <td className="text-end">
                  <a className="link" onClick={() => dispatch(setAccountPasswordModal(true))}>
                    Set password
                  </a>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!childAccount && (
          <div className="account-sso-row">
            <div>
              <div className="bold">Enterprise SSO</div>
              <div className="font-small grey">Secure company access with your identity provider</div>
            </div>
            <div className="text-end flex-shrink-0 ms-2">
              {ssoLoading || ssoOpeningSetup || ssoChecking ? (
                <Spinner animation="border" size="sm" />
              ) : ssoConnected ? (
                <div className="flex align-items-center">
                  <span className="account-sso-connected me-2">Connected</span>
                  <a onClick={() => handleOpenSsoSetup()} className="link color-purple">
                    Edit
                  </a>
                </div>
              ) : ssoEntitled && ssoSetupOpened ? (
                <a onClick={handleFinishedSsoSetup} className="link color-purple">
                  Check connection status
                </a>
              ) : (
                <Button
                  size="sm"
                  className="action-btn"
                  variant="outline-primary"
                  onClick={ssoEntitled ? () => handleOpenSsoSetup() : openEnterpriseEnquiryCalendar}
                >
                  Enable
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="account-section">
        <div className="account-section-title">Plan & billing</div>
        <table className="account-table w-100 grey">
          <tbody>
            {!trialAccount && !childAccount && (
              <tr>
                <td>Status</td>
                <td className="text-end font-weight-bold">
                  {displayStatus || 'Incomplete'}{' '}
                  {isCancelling && !isFreeTrial && (
                    <span>
                      (
                      <a className="link color-purple" onClick={handleUncancelClick}>
                        Uncancel
                      </a>
                      )
                    </span>
                  )}
                  {downgradePlan && !isCancelling && !isFreeTrial && (
                    <span className="downgrade-scheduled-label ms-1 text-muted">(Downgrade scheduled)</span>
                  )}
                </td>
              </tr>
            )}
            <tr>
              <td>Current plan</td>
              {!trialAccount && !isFreeTrial && (
                <td className="text-end bold">
                  {planLabel ? `${planLabel} - ` : ''} {currency.toUpperCase()} {isIndia ? amount : amount / 100 || '-'}{' '}
                  / {interval || '-'}
                </td>
              )}
              {isFreeTrial && (
                <td className="text-end bold">
                  {planLabel ? `${planLabel} - ` : ''} {displayPrice}
                </td>
              )}
              {trialAccount && !isFreeTrial && <td className="text-end bold">Free</td>}
            </tr>
            {isFreeTrial && trialEndDate && (
              <tr>
                <td>{billingLabel}</td>
                <td className="text-end bold">{billingDate}</td>
              </tr>
            )}
            {nextBilling && status?.toUpperCase() === ACCOUNT_STATUS.ACTIVE && !isFreeTrial && !childAccount && (
              <tr>
                <td>Next billing date</td>
                <td className="text-end bold">
                  {isNaN(nextBilling) ? nextBilling : moment.unix(nextBilling).format('DD MMM YY')}
                </td>
              </tr>
            )}
            {!trialAccount && !childAccount && (
              <tr>
                <td>Payment method</td>
                <td className="text-end bold">
                  {cardType && `${cardType.toUpperCase()}`} {lastFour && `•••• ${lastFour}`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!childAccount && (
          <div className="account-section-actions">
            <div className="flex flex-column">
              {stripeCustomer && (
                <a className="link color-purple text-decoration-none" onClick={fetchBillingLink}>
                  Manage billing →
                </a>
              )}
              {!stripeCustomer && !trialAccount && (
                <a className="link color-purple text-decoration-none" href={updateUrl} target="_blank" rel="noreferrer">
                  Update details →
                </a>
              )}
              {canCancelSubscription(status) && !isFreeTrial && (
                <a className="link color-purple text-decoration-none" onClick={handleCancelClick}>
                  Cancel subscription →
                </a>
              )}
              {isFreeTrial && (
                <a className="link color-purple text-decoration-none" onClick={handleCancelTrialClick}>
                  Cancel free trial →
                </a>
              )}
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              className="flex-shrink-0"
              onClick={() => onUpgrade(false)}
              disabled={isFreeTrial}
            >
              {productId ? 'Change plan' : 'Upgrade'} →
            </Button>
          </div>
        )}
      </div>

      <div className="account-section">
        <div className="account-section-title">Developer</div>
        <table className="account-table w-100 grey">
          <tbody>
            <tr>
              <td>
                API Key{' '}
                <span className="ms-2 small">
                  <a
                    href="https://api-docs.tiiny.host"
                    target="_blank"
                    rel="noreferrer"
                    className="link font-small text-decoration-none"
                  >
                    View docs
                  </a>
                </span>
              </td>
              {hasFeature(productId, F_API_KEY) && (
                <td className="text-end font-weight-bold">
                  {apiKey ? (
                    <div className="account-api-key">
                      <span className="account-api-key-value text-truncate">{apiKey}</span>
                      <CustomTooltip label="Copy key">
                        <button
                          type="button"
                          className="account-icon-btn"
                          aria-label="Copy key"
                          onClick={() => copyToClipboard(apiKey)}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <rect x="9" y="9" width="12" height="12" rx="2" />
                            <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
                          </svg>
                        </button>
                      </CustomTooltip>
                      <CustomTooltip label="Recycle key">
                        <button
                          type="button"
                          className="account-icon-btn"
                          aria-label="Recycle key"
                          onClick={() => setShowRecycleModal(true)}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
                            <path d="M3 21v-5h5" />
                            <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
                            <path d="M21 3v5h-5" />
                          </svg>
                        </button>
                      </CustomTooltip>
                    </div>
                  ) : (
                    <>
                      {generatingKey && <Spinner animation="border" size="sm" />}
                      {!generatingKey && (
                        <a onClick={generateAPIKey} className="link color-purple">
                          Generate
                        </a>
                      )}
                    </>
                  )}
                </td>
              )}
              {!hasFeature(productId, F_API_KEY) && (
                <td className="text-end font-small">
                  <i>Requires upgrade</i>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      {childAccount && (
        <div className="alert alert-primary mt-3" role="alert">
          This is a team member account, please contact your admin for any modifications
        </div>
      )}
    </>
  )
}

const mapDispatchToProps = (dispatch) => ({ dispatch })

export default connect(undefined, mapDispatchToProps)(Details)
